import { List } from "@lightningjs/ui";
import { Colors, Img, Lightning, Router, Utils, Storage } from "@lightningjs/sdk";
import { MENU_WIDTH, MENU_WIDTH_WO_OFFSET, PAGES, PLATFORMS, STORAGE_KEYS } from "../../lib/utils";
import { MenuItem } from "./components/MenuItem";
import theme from "../../lib/theme";
import configs from "../../lib/configs";
import posthog from "posthog-js";

interface MenuTemplateSpec extends Lightning.Component.TemplateSpec {
    Logo: object;
    List: typeof List;
    BottomList: typeof List;
    Shadow: object;
    Border: {
        TopLeft: object;
        BottomLeft: object;
        TopRight: object;
        BottomRight: object;
    };
    Background: object;
}

export class Menu
    extends Lightning.Component<MenuTemplateSpec>
    implements Lightning.Component.ImplementTemplateSpec<MenuTemplateSpec>
{
    _activePage: undefined | string = configs.defaultPage;
    _items: any[] = [];
    _bottomItems: any[] = [];

    _boundEventHandler?: any;

    _hideMenuHandler: () => void = () => {};
    _showMenuHandler: () => void = () => {};

    _isFullWidth = configs.layout?.menu.fullWidth;
    static _shadow = configs.layout?.menu.shadow;

    static override _template(): Lightning.Component.Template<MenuTemplateSpec> {
        return {
            w: MENU_WIDTH,
            h: theme.layout.h,
            rect: true,
            collision: true,
            color: Colors(theme.colors.bg.secondary).get(),
            Shadow: this._shadow
                ? {
                      h: theme.layout.h,
                      w: MENU_WIDTH_WO_OFFSET + theme.menu.offset,
                      x: theme.menu.offset / 2,
                      color: Colors("#000").get(),
                      texture: Lightning.Tools.getShadowRect(
                          MENU_WIDTH_WO_OFFSET + theme.menu.offset,
                          theme.layout.h,
                          0,
                          theme.menu.offset,
                          theme.menu.offset
                      )
                  }
                : undefined,
            Background: {
                w: MENU_WIDTH_WO_OFFSET,
                x: theme.menu.offset,
                h: theme.layout.h,
                color: Colors(theme.colors.bg.secondary).get(),
                rect: true
            },
            Border: this._shadow
                ? {
                      TopLeft: {
                          y: theme.layout.h / 3,
                          h: theme.layout.h / 6,
                          x: theme.menu.offset,
                          w: 3,
                          rect: true,
                          colorTop: Colors(theme.colors.bg.secondary).get(),
                          colorBottom: Colors("#fff").alpha(0.57).get()
                      },
                      BottomLeft: {
                          y: (theme.layout.h / 3) * 2 - theme.layout.h / 6,
                          h: theme.layout.h / 6,
                          x: theme.menu.offset,
                          w: 3,
                          rect: true,
                          colorBottom: Colors(theme.colors.bg.secondary).get(),
                          colorTop: Colors("#fff").alpha(0.57).get()
                      },
                      TopRight: {
                          y: theme.layout.h / 3,
                          h: theme.layout.h / 6,
                          x: MENU_WIDTH - 3,
                          w: 3,
                          rect: true,
                          colorTop: Colors(theme.colors.bg.secondary).get(),
                          colorBottom: Colors("#fff").alpha(0.57).get()
                      },
                      BottomRight: {
                          y: (theme.layout.h / 3) * 2 - theme.layout.h / 6,
                          h: theme.layout.h / 6,
                          x: MENU_WIDTH - 3,
                          w: 3,
                          rect: true,
                          colorBottom: Colors(theme.colors.bg.secondary).get(),
                          colorTop: Colors("#fff").alpha(0.57).get()
                      }
                  }
                : undefined,
            Logo: {
                mountX: 0.5,
                x: MENU_WIDTH_WO_OFFSET / 2 + theme.menu.offset,
                y: 20,
                // pivotX: -0.35,
                pivotY: 0.25,
                scale: configs.layout?.menu.fullWidth ? 1 : 0.5,
                texture: Img(Utils.asset("images/logo.png")).contain(theme.logo.w, theme.logo.h)
            },
            List: {
                y: 240,
                x: theme.menu.item.offset + theme.menu.offset,
                collision: true,
                type: List,
                direction: "column",
                spacing: 12
            },
            BottomList: {
                y: 800,
                x: theme.menu.item.offset + theme.menu.offset,
                collision: true,
                type: List,
                direction: "column",
                spacing: 12
            }
        };
    }

    readonly List = this.getByRef("List")!;
    readonly BottomList = this.getByRef("BottomList")!;

    override _setup() {
        this._boundEventHandler = (args: any) => {
            this._setActivePage(this, args);
        };

        this._hideMenuHandler = () => this.hideMenu();
        this._showMenuHandler = () => this.showMenu();

        this._items = [];
        this._bottomItems = [];
        if (configs.modules.search) {
            this._items.push({
                type: MenuItem,
                page: PAGES.search,
                icon: "search",
                label: "nav.search",
                activeId: this._activePage
            });
        }

        // if (configs.modules.hoteltv) {
        //     this._items.push({
        //         type: MenuItem,
        //         page: PAGES.hoteltv,
        //         icon: "home",
        //         label: "nav.home",
        //         activeId: this._activePage
        //     });
        // }

        if (configs.modules.livetv) {
            this._items.push({
                type: MenuItem,
                page: PAGES.live,
                icon: "live",
                label: "nav.live",
                activeId: this._activePage
            });
        }

        if (configs.modules.vod) {
            this._items.push({
                type: MenuItem,
                page: PAGES.vod,
                icon: configs.defaultPage === "vod" ? "home" : "vod",
                label: configs.defaultPage === "vod" ? "nav.home" : "nav.browse",
                activeId: this._activePage
            });
        }

        if (configs.modules.movies) {
            this._items.push({
                type: MenuItem,
                page: PAGES.movies,
                icon: "movies",
                label: "nav.movies",
                activeId: this._activePage
            });
        }

        if (configs.modules.series) {
            this._items.push({
                type: MenuItem,
                page: PAGES.series,
                icon: "series",
                label: "nav.series",
                activeId: this._activePage
            });
        }

        if (configs.modules.documentaries) {
            this._items.push({
                type: MenuItem,
                page: PAGES.documentaries,
                icon: 'configs.defaultPage === "vod" ? "nav.home" : ',
                label: "nav.documentaries",
                activeId: this._activePage
            });
        }

        if (configs.modules.apps && window.Android && window.Android.getAppList) {
            this._items.push({
                type: MenuItem,
                page: PAGES.apps,
                icon: "apps",
                label: "nav.apps",
                activeId: this._activePage
            });
        }

        if (configs.modules.bookmarks) {
            this._items.push({
                type: MenuItem,
                page: PAGES.favorite,
                icon: "bookmarks",
                label: "nav.bookmarks",
                activeId: this._activePage
            });
        }

        this._bottomItems.push({
            type: MenuItem,
            page: PAGES.info,
            icon: "info",
            label: "nav.info"
            // activeId: this._activePage
        });
        if (configs.modules.settings) {
            this._bottomItems.push({
                type: MenuItem,
                page: PAGES.settings,
                icon: "settings",
                label: "nav.settings"
                // activeId: this._activePage
            });
        }
        this._bottomItems.push({
            type: MenuItem,
            page: PAGES.exit,
            icon: "exit",
            label: "nav.exit"
            // activeId: this._activePage
        });

        this.List.patch({
            items: this._items
        });

        this.BottomList.patch({
            // 80 = approx rest of the offset, paddings, spacing
            y: theme.layout.h - MenuItem._bgSizeH * this._bottomItems.length - 80,
            items: this._bottomItems
        });

        for (const item of this.BottomList.items) {
            item.activeId = this._activePage;
        }

        for (const wrapper of [...this.List.children, ...this.BottomList.children]) {
            wrapper?.patch({
                collision: true
            });
        }

        // Samsung Tizen specific fallback for logo rendering
        try {
            const platform = Storage.get(STORAGE_KEYS.platform);
            if (platform === PLATFORMS.samsung) {
                this.patch({
                    Logo: {
                        mountX: 0.5,
                        x: MENU_WIDTH / 2,
                        y: 20,
                        // pivotX: -0.35,
                        pivotY: 0.25,
                        scale: 0.5,
                        w: theme.logo.w,
                        h: theme.logo.h,
                        // Older Tizen WebKit has issues with Img().contain textures occasionally
                        // Use direct src-based image rendering as a fallback
                        texture: undefined,
                        src: Utils.asset("images/logo.png")
                    }
                });
            }
        } catch (e) {
            console.error(e);
        }
    }

    override _getFocused() {
        return this.List;
    }

    override _handleUp() {
        // needed to prevent focus on page
    }

    override _handleLeft() {
        // needed to prevent focus on page
    }

    override _handleRight() {
        Router.focusPage();
    }

    override _handleDown() {
        this._setState("SettingsState");
    }

    override _attach() {
        this.application.on("setActivePage", this._boundEventHandler);
        this.application.on("hideMenu", this._hideMenuHandler);
        this.application.on("showMenu", this._showMenuHandler);
    }

    override _detach() {
        this.application.off("setActivePage", this._boundEventHandler);
        this.application.off("hideMenu", this._hideMenuHandler);
        this.application.off("showMenu", this._showMenuHandler);
    }

    override _focus() {
        if (!configs.layout?.menu.fullWidth) {
            this.patch({
                smooth: {
                    w: theme.menu.activeW
                },
                Logo: {
                    smooth: {
                        scale: 1,
                        x: theme.menu.activeW / 2
                    }
                }
            });
        }

        this._updateList();

        try {
            const index = this.List.items.findIndex((i: any) => i.isActive && i.isActive());

            if (index === -1) {
                this._setState("SettingsState");
                this.List.setIndex(this._items.length - 1);
            } else {
                this._setState("");
                this.List.setIndex(index);
            }
        } catch (e) {
            console.log(e);
        }
    }

    override _unfocus() {
        if (!this._isFullWidth) {
            this.patch({
                smooth: {
                    w: MENU_WIDTH
                },
                Logo: {
                    smooth: {
                        x: MENU_WIDTH / 2,
                        scale: 0.5
                    }
                }
            });
        }

        this._updateList();
    }

    private _setActivePage(self: any = this, id: string) {
        const menuPage = Object.values(PAGES).find((p) => p.name === id);
        if (menuPage) {
            posthog.capture("MenuItemOpened", {
                menu: menuPage.name,
                timestamp: new Date().toISOString()
            });
        }

        this._updateList(id);
    }

    _updateList(activePage?: string) {
        for (const item of [...this.List.items, ...this.BottomList.items]) {
            item.setFocus && item.setFocus(this.hasFocus());
            if (typeof item.setActiveId !== "undefined") {
                item.setActiveId(activePage);
            }
        }
    }

    static override _states() {
        return [
            class MenuState extends this {},
            class SettingsState extends this {
                override _getFocused() {
                    return this.BottomList;
                }

                override _handleUp() {
                    this._setState("MenuState");
                }

                override $exit() {
                    this.List.setIndex(this._items.length - 1);
                }
            }
        ];
    }

    private hideMenu() {
        this.patch({ smooth: { alpha: 0 } });
    }

    private showMenu() {
        this.patch({ smooth: { alpha: 1 } });
    }
}
